import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { ExporterInfo, Job, QualityReport, ValidationFinding, ValidationReport } from "../api/types";
import { useApp } from "../state/AppContext";
import { Badge, KeyValue, ProgressBar, Spinner } from "./ui";

/**
 * Validate, measure, export.
 *
 * The order on screen is the order that matters: a session that does not
 * validate should not be exported, and a session whose quality is unknown
 * should not be trained on. Both run before the export buttons rather than
 * being offered as an afterthought.
 */
export function ExportPanel({ sessionId, jobs }: { sessionId: string; jobs: Job[] }) {
  const { notify } = useApp();
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [quality, setQuality] = useState<QualityReport | null>(null);
  const [exporters, setExporters] = useState<ExporterInfo[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, any>>({});

  const refresh = useCallback(async () => {
    setValidation(await api.validate(sessionId).catch(() => null));
    setQuality(await api.quality(sessionId).catch(() => null));
    const list = await api.exporters(sessionId).catch(() => null);
    if (list) setExporters(list.exporters);
  }, [sessionId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const finished = jobs
    .filter((j) => j.session_id === sessionId && (j.kind === "export" || j.kind === "quality_report") && j.state === "succeeded")
    .map((j) => j.id)
    .join(",");
  useEffect(() => {
    if (!finished) return;
    void refresh();
    for (const job of jobs) {
      if (job.session_id === sessionId && job.kind === "export" && job.state === "succeeded" && job.result?.destination) {
        setResults((prev) => ({ ...prev, [job.params.exporter_id]: job.result }));
      }
    }
  }, [finished, refresh, jobs]);

  const active = (kind: string, exporterId?: string) =>
    jobs.find(
      (j) =>
        j.session_id === sessionId && j.kind === kind &&
        (j.state === "queued" || j.state === "running") &&
        (!exporterId || j.params?.exporter_id === exporterId),
    );

  const run = async (exporter: ExporterInfo) => {
    setBusy(exporter.id);
    try {
      await api.runExport(sessionId, exporter.id, {download_archive: true});
      notify(`Exporting ${exporter.display_name}…`);
    } catch (e: any) {
      notify(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  const qualityJob = active("quality_report");

  return (
    <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
      {/* ---- validation ---- */}
      <div className="panel" style={{ padding: 0, flexShrink: 0 }}>
        <div className="panel-head" style={{ gap: 6 }}>
          <span style={{ textTransform: "none" }}>Canonical format</span>
          {validation ? (
            <Badge kind={validation.ok ? "good" : "invalid"}>
              {validation.ok ? "valid" : `${validation.errors} error(s)`}
            </Badge>
          ) : null}
          <span className="spacer" />
          <button className="ghost" onClick={() => void refresh()}>Recheck</button>
          <button
            className="ghost"
            title="Re-read the source files and compare them against the recorded checksums"
            onClick={async () => {
              setBusy("verify");
              setValidation(await api.validate(sessionId, true).catch(() => null));
              setBusy(null);
            }}
          >
            {busy === "verify" ? <Spinner size={10} /> : null} Verify checksums
          </button>
        </div>
        <div style={{ padding: 10 }}>
          {!validation ? (
            <div className="hint">Checking…</div>
          ) : (
            <>
              <KeyValue
                rows={[
                  ["Schema", String(validation.checked.schema_version ?? "—")],
                  ["Streams", String(validation.checked.streams ?? "—")],
                  ["Frames", Object.entries(validation.checked.frames ?? {})
                    .map(([k, v]) => `${k.split(".")[1]} ${v}`).join(" · ") || "—"],
                  ["Annotations", String(validation.checked.annotations ?? 0)],
                  ["Derived results", String(validation.checked.derived ?? 0)],
                ]}
              />
              <Findings findings={validation.findings} />
            </>
          )}
        </div>
      </div>

      {/* ---- quality ---- */}
      <div className="panel" style={{ padding: 0, flexShrink: 0 }}>
        <div className="panel-head" style={{ gap: 6 }}>
          <span style={{ textTransform: "none" }}>Data quality</span>
          {quality ? (
            <Badge kind={quality.verdict === "good" ? "good" : quality.verdict === "unusable" ? "invalid" : "warning"}>
              {quality.verdict.replace(/_/g, " ")}
            </Badge>
          ) : (
            <Badge kind="neutral">not measured</Badge>
          )}
          <span className="spacer" />
          {qualityJob ? (
            <div className="row" style={{ width: 130 }}>
              <Spinner size={10} />
              <ProgressBar value={qualityJob.progress} state={qualityJob.state} />
            </div>
          ) : (
            <button onClick={() => api.runQuality(sessionId).then(() => notify("Measuring quality…"))}>
              {quality ? "Re-measure" : "Measure"}
            </button>
          )}
        </div>
        <div style={{ padding: 10 }}>
          {!quality ? (
            <div className="hint">
              Samples frames from the originals and measures focus, exposure and motion, alongside
              dropped frames, sync and calibration state.
            </div>
          ) : (
            <>
              {Object.entries(quality.streams).map(([id, stream]: [string, any]) => (
                <div key={id} style={{ marginBottom: 10 }}>
                  <div className="mono tiny" style={{ fontWeight: 600, marginBottom: 4 }}>{id}</div>
                  <KeyValue
                    rows={[
                      ["Frames", `${stream.frames} · ${stream.measured_fps?.toFixed(3)} fps measured`],
                      ["Interval", `${stream.interval_ms.min?.toFixed(1)}–${stream.interval_ms.max?.toFixed(1)} ms (median ${stream.interval_ms.median?.toFixed(1)})`],
                      ["Dropped", `${stream.dropped_frame_gaps} gap(s), ~${stream.estimated_missing_frames} frame(s)`],
                      ["Focus", `median ${stream.image.focus.median?.toFixed(0)} · ${stream.image.blurred_frames}/${stream.image.sampled_frames} below the floor`],
                      ["Brightness", `median ${stream.image.brightness.median?.toFixed(0)} · ${stream.image.dark_frames} dark, ${stream.image.bright_frames} bright`],
                    ]}
                  />
                </div>
              ))}
              <Findings findings={quality.findings} />
            </>
          )}
        </div>
      </div>

      {/* ---- exporters ---- */}
      <div className="panel" style={{ padding: 0, flexShrink: 0 }}>
        <div className="panel-head"><span style={{ textTransform: "none" }}>Export</span></div>
        <div style={{ padding: 10 }}>
          {validation && !validation.ok ? (
            <div className="error-box tiny" style={{ marginBottom: 10 }}>
              Exporting is blocked while the session does not validate — the problem would only get
              harder to see downstream.
            </div>
          ) : null}
          {exporters.map((exporter) => {
            const job = active("export", exporter.id);
            const result = results[exporter.id];
            return (
              <div key={exporter.id} style={{ marginBottom: 12 }}>
                <div className="row" style={{ gap: 6 }}>
                  <span style={{ fontWeight: 600 }}>{exporter.display_name}</span>
                  {exporter.state !== "available" ? <Badge kind="warning">unavailable</Badge> : null}
                  <span className="spacer" />
                  {job ? (
                    <div className="row" style={{ width: 120 }}>
                      <Spinner size={10} />
                      <ProgressBar value={job.progress} state={job.state} />
                    </div>
                  ) : (
                    <button
                      disabled={
                        exporter.state !== "available" || busy === exporter.id ||
                        (validation != null && !validation.ok)
                      }
                      onClick={() => void run(exporter)}
                    >
                      Export
                    </button>
                  )}
                </div>
                {exporter.description ? (
                  <div className="tiny muted" style={{ marginTop: 3 }}>{exporter.description}</div>
                ) : null}
                {exporter.state !== "available" ? (
                  <div className="tiny" style={{ color: "var(--warn)", marginTop: 3 }}>{exporter.reason}</div>
                ) : null}
                {result ? (
                  <div className="tiny muted mono" style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                    {result.download_url ? <a href={result.download_url} download>Download {exporter.id} archive ↓</a> : `→ ${result.destination}`}
                    {result.frames ? ` · ${result.frames} frames @ ${result.fps} fps` : ""}
                    {result.messages ? ` · ${Object.values(result.messages).reduce((a: any, b: any) => a + b, 0)} messages` : ""}
                    {result.has_action === false ? " · observational (no action/state)" : ""}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Findings({ findings }: { findings: ValidationFinding[] }) {
  if (findings.length === 0) {
    return <div className="tiny muted" style={{ marginTop: 8 }}>Nothing to report.</div>;
  }
  const colour = (level: string) =>
    level === "error" ? "var(--bad)" : level === "warning" ? "var(--warn)" : "var(--text-2)";
  return (
    <div className="col" style={{ gap: 4, marginTop: 8 }}>
      {findings.map((finding, i) => (
        <div key={i} className="tiny" style={{ color: colour(finding.level), lineHeight: 1.45 }}>
          <span className="mono" style={{ opacity: 0.7 }}>[{finding.level}]</span> {finding.message}
        </div>
      ))}
    </div>
  );
}
